import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, ShieldAlert, Trash2, Plus, FileText, CreditCard, StickyNote, Calendar, CalendarCheck, Mail, Landmark, Video } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Eyebrow } from "@/components/motion";

const inputCls = "w-full bg-[#171A22] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all text-sm";

const ALL_PLANS = [
  { id: null, name: "No plan" },
  { id: "m-starter", name: "Skool Community ($97/mo)" },
  { id: "m-pro", name: "Prestigious ($189/mo)" },
  { id: "m-vip", name: "Diamond ($299/mo)" },
  { id: "f-express", name: "Basic ($999/yr)" },
  { id: "f-overhaul", name: "Prestigious ($1,499/yr)" },
  { id: "f-bundle", name: "Diamond ($2,499/yr)" },
];

const STATUSES = ["In Progress", "Deleted", "Escalated", "Verified"];
const STATUS_STYLES = {
  "Deleted": "bg-emerald/10 text-emerald border-emerald/30",
  "In Progress": "bg-gold/10 text-gold border-gold/30",
  "Escalated": "bg-red-500/10 text-red-400 border-red-500/30",
  "Verified": "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
};

function Guard({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }
  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="admin-access-denied">
        <div className="text-center bg-[#101218] border border-white/10 rounded-2xl p-12 max-w-md">
          <ShieldAlert className="w-10 h-10 text-red-400 mx-auto mb-5" />
          <h1 className="font-serif text-2xl text-white mb-3">Admin access only</h1>
          <p className="text-slate-400 text-sm">
            {user ? "This account doesn't have admin privileges." : "Sign in with an admin account to continue."}
          </p>
        </div>
      </div>
    );
  }
  return children;
}

function ClientDetail({ clientId, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [newDispute, setNewDispute] = useState({ item: "", bureau: "Equifax", amount: "" });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/clients/${clientId}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [clientId]);

  useEffect(() => { setDetail(null); load(); }, [load]);

  const refresh = async () => { await load(); onChanged(); };

  const setPlan = async (planId) => {
    try {
      await api.put(`/admin/clients/${clientId}/plan`, { plan_id: planId || null });
      toast.success("Plan updated");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setDisputeStatus = async (d, status) => {
    try {
      await api.put(`/admin/disputes/${d.id}`, { item: d.item, bureau: d.bureau, status, amount: d.amount, filed: d.filed });
      toast.success(`Marked "${d.item}" as ${status}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeDispute = async (d) => {
    try {
      await api.delete(`/admin/disputes/${d.id}`);
      toast.success("Dispute removed");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addDispute = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/clients/${clientId}/disputes`, {
        item: newDispute.item, bureau: newDispute.bureau, amount: newDispute.amount || "—",
      });
      toast.success("Dispute added");
      setNewDispute({ item: "", bureau: "Equifax", amount: "" });
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await api.post(`/admin/clients/${clientId}/notes`, { text: note });
      toast.success("Note saved");
      setNote("");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeNote = async (id) => {
    try {
      await api.delete(`/admin/notes/${id}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!detail) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="admin-detail-loading">
        <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  const { client, plan, disputes, notes, orders, score_history } = detail;
  const latest = score_history[score_history.length - 1];
  const avgScore = latest ? Math.round((latest.equifax + latest.experian + latest.transunion) / 3) : null;

  return (
    <div className="space-y-6" data-testid="admin-client-detail">
      {/* Profile */}
      <div className="bg-[#101218] border border-white/10 rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-serif text-2xl text-white tracking-tight" data-testid="admin-client-name">{client.name}</h2>
            <p className="text-slate-400 text-sm mt-1" data-testid="admin-client-email">{client.email}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {avgScore && (
                <span className="bg-emerald/10 text-emerald border border-emerald/30 px-3 py-1 rounded-full text-xs font-mono tracking-widest" data-testid="admin-client-score">
                  Avg Score {avgScore}
                </span>
              )}
              <span className="bg-white/5 border border-white/10 text-slate-400 px-3 py-1 rounded-full text-xs font-mono tracking-widest">
                Joined {client.created_at ? new Date(client.created_at).toLocaleDateString() : "—"}
              </span>
            </div>
          </div>
          <div className="min-w-[220px]">
            <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Active Plan</label>
            <select
              value={client.plan_id || ""}
              onChange={(e) => setPlan(e.target.value)}
              data-testid="admin-plan-select"
              className={`${inputCls} cursor-pointer`}
            >
              {ALL_PLANS.map((p) => (
                <option key={p.id || "none"} value={p.id || ""}>{p.name}</option>
              ))}
            </select>
            {plan && (
              <p className="mt-2 text-xs text-gold font-mono" data-testid="admin-current-plan">
                {plan.name} · ${plan.price.toLocaleString()}{plan.type === "monthly" ? "/mo" : " flat"}
              </p>
            )}
          </div>
        </div>
        {orders.length > 0 && (
          <div className="mt-5 pt-5 border-t border-white/5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
              <CreditCard className="w-3.5 h-3.5" /> Order History
            </p>
            <div className="space-y-2">
              {orders.map((o, i) => (
                <div key={i} className="flex justify-between text-sm" data-testid={`admin-order-${i}`}>
                  <span className="text-slate-300">{o.plan_name}</span>
                  <span className="font-mono text-xs text-slate-500">${o.price.toLocaleString()} · {o.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Disputes */}
      <div className="bg-[#101218] border border-white/10 rounded-2xl p-6 md:p-8">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-5 flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" /> Disputed Items ({disputes.length})
        </p>
        <div className="space-y-3 mb-6">
          {disputes.length === 0 && <p className="text-slate-500 text-sm">No disputes on file yet.</p>}
          {disputes.map((d) => (
            <div key={d.id} data-testid={`admin-dispute-row-${d.id}`}
              className="flex flex-col md:flex-row md:items-center gap-3 bg-elevated border border-white/10 rounded-xl px-4 py-3.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-200 truncate">{d.item}</p>
                <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">
                  {d.bureau} · {d.amount} · filed {d.filed}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={d.status}
                  onChange={(e) => setDisputeStatus(d, e.target.value)}
                  data-testid={`admin-dispute-status-${d.id}`}
                  className={`text-[11px] font-mono uppercase tracking-widest border rounded-full px-3 py-1.5 bg-transparent cursor-pointer focus:outline-none ${STATUS_STYLES[d.status] || STATUS_STYLES["In Progress"]}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s} className="bg-elevated text-white">{s}</option>)}
                </select>
                <button onClick={() => removeDispute(d)} data-testid={`admin-dispute-delete-${d.id}`}
                  className="p-2 text-slate-500 hover:text-red-400 transition-colors" aria-label="Delete dispute">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={addDispute} className="grid grid-cols-1 md:grid-cols-[1fr_150px_120px_auto] gap-3" data-testid="admin-add-dispute-form">
          <input required placeholder="Item (e.g. Midland Credit — Collection)" value={newDispute.item}
            data-testid="admin-dispute-item-input"
            onChange={(e) => setNewDispute({ ...newDispute, item: e.target.value })} className={inputCls} />
          <select value={newDispute.bureau} data-testid="admin-dispute-bureau-select"
            onChange={(e) => setNewDispute({ ...newDispute, bureau: e.target.value })} className={inputCls}>
            {["Equifax", "Experian", "TransUnion"].map((b) => <option key={b}>{b}</option>)}
          </select>
          <input placeholder="Amount" value={newDispute.amount} data-testid="admin-dispute-amount-input"
            onChange={(e) => setNewDispute({ ...newDispute, amount: e.target.value })} className={inputCls} />
          <button type="submit" disabled={saving} data-testid="admin-dispute-add-button"
            className="flex items-center justify-center gap-2 bg-gold text-obsidian hover:bg-gold-light font-mono text-xs uppercase tracking-[0.12em] px-5 py-3 rounded-xl transition-all disabled:opacity-50">
            <Plus className="w-4 h-4" /> Add
          </button>
        </form>
      </div>

      {/* Notes */}
      <div className="bg-[#101218] border border-white/10 rounded-2xl p-6 md:p-8">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-5 flex items-center gap-2">
          <StickyNote className="w-3.5 h-3.5" /> Internal Notes ({notes.length})
        </p>
        <form onSubmit={addNote} className="flex gap-3 mb-5" data-testid="admin-add-note-form">
          <textarea placeholder="Add a private note about this client…" value={note} rows={2}
            data-testid="admin-note-input" onChange={(e) => setNote(e.target.value)}
            className={`${inputCls} resize-none flex-1`} />
          <button type="submit" data-testid="admin-note-add-button"
            className="self-end flex items-center gap-2 bg-gold text-obsidian hover:bg-gold-light font-mono text-xs uppercase tracking-[0.12em] px-5 py-3 rounded-xl transition-all">
            <Plus className="w-4 h-4" /> Save
          </button>
        </form>
        <div className="space-y-3">
          {notes.length === 0 && <p className="text-slate-500 text-sm">No notes yet.</p>}
          {notes.map((n) => (
            <div key={n.id} data-testid={`admin-note-${n.id}`}
              className="flex items-start justify-between gap-4 bg-elevated border border-white/10 rounded-xl px-4 py-3.5">
              <div>
                <p className="text-sm text-slate-200 whitespace-pre-wrap">{n.text}</p>
                <p className="font-mono text-[10px] text-slate-500 mt-1.5">{new Date(n.created_at).toLocaleString()}</p>
              </div>
              <button onClick={() => removeNote(n.id)} data-testid={`admin-note-delete-${n.id}`}
                className="p-1.5 text-slate-500 hover:text-red-400 transition-colors shrink-0" aria-label="Delete note">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const BOOKING_STATUS_STYLES = {
  requested: "bg-gold/10 text-gold border-gold/30",
  confirmed: "bg-emerald/10 text-emerald border-emerald/30",
  completed: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  cancelled: "bg-red-500/10 text-red-400 border-red-500/30",
};

function InboxShell({ testId, label, count, children }) {
  return (
    <div className="bg-[#101218] border border-white/10 rounded-2xl p-6 md:p-8" data-testid={testId}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-6">{label} ({count ?? "…"})</p>
      {children}
    </div>
  );
}

function EmptyState({ text }) {
  return <p className="text-slate-500 text-sm text-center py-10">{text}</p>;
}

function BookingsTab() {
  const [bookings, setBookings] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/bookings"); setBookings(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (b, status) => {
    try {
      await api.put(`/admin/bookings/${b.id}/status`, { status });
      toast.success(`Booking marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <InboxShell testId="admin-bookings-tab" label="Booked Calls" count={bookings?.length}>
      {!bookings ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" /></div>
      ) : bookings.length === 0 ? <EmptyState text="No calls booked yet." /> : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`admin-booking-${b.id}`}
              className="flex flex-col lg:flex-row lg:items-center gap-4 bg-elevated border border-white/10 rounded-xl px-5 py-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-white font-medium">{b.name}</p>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-gold">{b.service}</span>
                </div>
                <p className="font-mono text-xs text-slate-400 mt-1.5">{b.date} at {b.time} ET</p>
                <p className="text-xs text-slate-500 mt-1">
                  {b.email} · {b.phone}{b.notes ? ` · "${b.notes}"` : ""}
                </p>
                {b.meet_link && (
                  <a href={b.meet_link} target="_blank" rel="noopener noreferrer" data-testid={`admin-booking-meet-${b.id}`}
                    className="inline-flex items-center gap-1.5 mt-2 text-xs text-gold hover:text-gold-light font-mono transition-colors">
                    <Video className="w-3.5 h-3.5" /> Google Meet link
                  </a>
                )}
              </div>
              <select value={b.status} onChange={(e) => setStatus(b, e.target.value)}
                data-testid={`admin-booking-status-${b.id}`}
                className={`self-start lg:self-center text-[11px] font-mono uppercase tracking-widest border rounded-full px-3 py-1.5 bg-transparent cursor-pointer focus:outline-none ${BOOKING_STATUS_STYLES[b.status] || BOOKING_STATUS_STYLES.requested}`}>
                {["requested", "confirmed", "completed", "cancelled"].map((s) => (
                  <option key={s} value={s} className="bg-elevated text-white">{s}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </InboxShell>
  );
}

function PrequalsTab() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/prequals").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <InboxShell testId="admin-prequals-tab" label="Funding Pre-Qualifications" count={items?.length}>
      {!items ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" /></div>
      ) : items.length === 0 ? <EmptyState text="No pre-qualification requests yet." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`admin-prequal-${p.id}`} className="bg-elevated border border-white/10 rounded-xl p-5">
              <div className="flex justify-between items-start gap-3">
                <p className="text-white font-medium">{p.name}</p>
                <span className="bg-emerald/10 text-emerald border border-emerald/30 px-2.5 py-0.5 rounded-full text-[10px] font-mono tracking-widest">
                  Score {p.score}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">{p.email}</p>
              <div className="flex flex-wrap gap-2 mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                <span className="bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">${Number(p.revenue).toLocaleString()}/mo revenue</span>
                <span className="bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">{p.time_in_business}</span>
              </div>
              <p className="font-mono text-[10px] text-slate-600 mt-3">{new Date(p.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}
    </InboxShell>
  );
}

function MessagesTab() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/messages").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <InboxShell testId="admin-messages-tab" label="Contact Messages" count={items?.length}>
      {!items ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" /></div>
      ) : items.length === 0 ? <EmptyState text="No contact messages yet." /> : (
        <div className="space-y-3">
          {items.map((m) => (
            <div key={m.id} data-testid={`admin-message-${m.id}`} className="bg-elevated border border-white/10 rounded-xl px-5 py-4">
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                  <p className="text-white font-medium">{m.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    <a href={`mailto:${m.email}`} className="text-gold hover:text-gold-light transition-colors">{m.email}</a>
                    {m.phone ? ` · ${m.phone}` : ""}
                  </p>
                </div>
                <span className="font-mono text-[10px] text-slate-600">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm text-slate-300 mt-3 leading-relaxed whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </InboxShell>
  );
}

function AdminPanel() {
  const [clients, setClients] = useState(null);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [calendar, setCalendar] = useState(null);
  const [tab, setTab] = useState("bookings");

  useEffect(() => {
    api.get("/calendar/status").then((r) => setCalendar(r.data)).catch(() => {});
    if (window.location.search.includes("calendar=connected")) {
      toast.success("Google Calendar connected. Bookings now create Meet links automatically.");
      window.history.replaceState({}, "", "/admin");
    } else if (window.location.search.includes("calendar=error")) {
      toast.error("Google Calendar connection failed. Try again.");
      window.history.replaceState({}, "", "/admin");
    }
  }, []);

  const connectCalendar = async () => {
    try {
      const { data } = await api.get("/calendar/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadClients = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/clients");
      setClients(data);
      setSelected((sel) => sel || (data[0]?.id ?? null));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const filtered = (clients || []).filter((c) =>
    `${c.name} ${c.email}`.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="py-16 md:py-24 px-6 lg:px-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10">
          <Eyebrow className="mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Admin Console</Eyebrow>
          <h1 className="font-serif text-3xl md:text-4xl text-white tracking-tight">Bookings & Messages</h1>
          <p className="text-slate-400 text-sm mt-2">Every booked call and message in one place.</p>
        </div>

        <div className="flex gap-2 mb-8 bg-surface border border-white/10 rounded-2xl p-1.5 w-fit flex-wrap" data-testid="admin-tabs">
          {[
            ["bookings", "Booked Calls", CalendarCheck],
            ["messages", "Messages", Mail],
          ].map(([key, label, Icon]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-mono text-xs uppercase tracking-[0.12em] transition-all ${
                tab === key ? "bg-gold text-obsidian" : "text-slate-400 hover:text-white"
              }`}>
              <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {tab === "bookings" && <BookingsTab />}
        {tab === "messages" && <MessagesTab />}
      </div>
    </div>
  );
}

export default function Admin() {
  return (
    <Guard>
      <AdminPanel />
    </Guard>
  );
}
